/**
 * <cutoff-countdown> — counts down to today's order cut-off time.
 *
 * Mirrors the product page's `dtf-ship-timer`: it runs until the cut-off, then
 * flips to an "after" state instead of rolling over to tomorrow, so the copy can
 * switch from "ships today" to "ships the next business day".
 *
 * data-hour            cut-off hour in 24h format (default 15 — 3 PM EST)
 * data-minute          cut-off minute (default 0)
 * data-timezone        IANA timezone the cut-off is defined in (default America/New_York)
 * data-skip-weekends   when present, non-working days count as past the cut-off
 *                      (weekends plus the closed dates in Theme settings)
 * data-labels          comma separated labels for hours,minutes,seconds (default "Hrs,Mins,Secs")
 *
 * Sets data-state="live"|"after" on itself and on the nearest ancestor carrying
 * `data-cutoff-scope`, so surrounding copy can react in CSS.
 */
/* The shop's closed days come from snippets/shipping-calendar, rendered in the
   head. Weekends-only is the fallback, so a missing snippet degrades to the old
   behaviour rather than breaking the badge. */
function shippingCalendar() {
  return (
    window.TidalShipping || {
      isClosed: function (d) {
        var day = d.getDay();
        return day === 0 || day === 6;
      },
      addBusinessDays: function (start, n) {
        var out = new Date(start);
        var added = 0;
        while (added < n) {
          out.setDate(out.getDate() + 1);
          if (!this.isClosed(out)) added++;
        }
        return out;
      },
    }
  );
}

if (!customElements.get('cutoff-countdown')) {
  customElements.define(
    'cutoff-countdown',
    class CutoffCountdown extends HTMLElement {
      connectedCallback() {
        this.hour = parseInt(this.dataset.hour, 10);
        if (Number.isNaN(this.hour)) this.hour = 15;

        this.minute = parseInt(this.dataset.minute, 10);
        if (Number.isNaN(this.minute)) this.minute = 0;

        this.timezone = this.dataset.timezone || 'America/New_York';
        this.skipWeekends = this.hasAttribute('data-skip-weekends');

        const labels = (this.dataset.labels || 'Hrs,Mins,Secs').split(',');
        this.labels = [labels[0] || 'Hrs', labels[1] || 'Mins', labels[2] || 'Secs'];

        this.scope = this.closest('[data-cutoff-scope]');

        this.render();
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      render() {
        this.innerHTML = `
          <span class="cutoff-countdown__digits">
            <span class="cutoff-countdown__unit">
              <span class="cutoff-countdown__value" data-hours>00</span>
              <span class="cutoff-countdown__unit-label">${this.labels[0]}</span>
            </span>
            <span class="cutoff-countdown__separator" aria-hidden="true">:</span>
            <span class="cutoff-countdown__unit">
              <span class="cutoff-countdown__value" data-minutes>00</span>
              <span class="cutoff-countdown__unit-label">${this.labels[1]}</span>
            </span>
            <span class="cutoff-countdown__separator" aria-hidden="true">:</span>
            <span class="cutoff-countdown__unit">
              <span class="cutoff-countdown__value" data-seconds>00</span>
              <span class="cutoff-countdown__unit-label">${this.labels[2]}</span>
            </span>
          </span>
        `;

        this.hoursEl = this.querySelector('[data-hours]');
        this.minutesEl = this.querySelector('[data-minutes]');
        this.secondsEl = this.querySelector('[data-seconds]');
      }

      /** "Now", expressed in the cut-off timezone. */
      getLocalNow() {
        return new Date(new Date().toLocaleString('en-US', { timeZone: this.timezone }));
      }

      setState(state) {
        if (this.state === state) return;
        this.state = state;
        this.setAttribute('data-state', state);
        if (this.scope) this.scope.setAttribute('data-state', state);
      }

      tick() {
        const now = this.getLocalNow();
        // Weekends and holidays alike: there is no "ships today" on a day the
        // shop is shut.
        const isClosed = shippingCalendar().isClosed(now);

        const cutoffSeconds = this.hour * 3600 + this.minute * 60;
        const nowSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
        const remaining = cutoffSeconds - nowSeconds;

        if (remaining <= 0 || (this.skipWeekends && isClosed)) {
          this.setState('after');
          return;
        }

        this.setState('live');

        this.hoursEl.textContent = String(Math.floor(remaining / 3600)).padStart(2, '0');
        this.minutesEl.textContent = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
        this.secondsEl.textContent = String(remaining % 60).padStart(2, '0');
      }
    }
  );
}
